import { useEffect, useState } from 'react';

// Session caches for the ODD lookups the projects list makes after it renders
// (STRY0038867). One request per key at a time, at most four in flight across
// all lookups, shared by every mounted list. A failed lookup caches null, so
// callers fall back instead of waiting forever.
const MAX_CONCURRENT = 4;
const waiting = [];
let running = 0;
const lookups = new Set();

// Forget every cached result and queued request. For tests, and for anything
// that must drop session data; keys already include what changes a result (an
// available-version key carries the project's baseline), so normal use never
// needs it.
export function resetOddLookups() {
  lookups.forEach(lookup => {
    lookup.values.clear();
    lookup.inFlight.clear();
  });
  waiting.length = 0;
  running = 0;
}

function schedule(task) {
  return new Promise(resolve => {
    const run = async () => {
      running += 1;
      try {
        await task();
      } finally {
        running -= 1;
        resolve();
        const next = waiting.shift();
        if (next) next();
      }
    };
    if (running < MAX_CONCURRENT) run();
    else waiting.push(run);
  });
}

export function createOddLookup(load) {
  const values = new Map();
  const inFlight = new Map();
  const ensure = (key, args) => {
    if (values.has(key)) return null;
    if (!inFlight.has(key)) {
      const pending = schedule(async () => {
        try {
          values.set(key, await load(...args));
        } catch (e) {
          values.set(key, null);
        } finally {
          inFlight.delete(key);
        }
      });
      inFlight.set(key, pending);
    }
    return inFlight.get(key);
  };
  const lookup = { values, inFlight, ensure };
  lookups.add(lookup);
  return lookup;
}

// Starts `lookup` for every project after render and re-renders as results land.
// `argsFor` must be a stable (module-level) function. Returns project => value,
// which is undefined while loading and null when the lookup failed.
export function useOddLookup(lookup, projects, argsFor) {
  const [, setLoaded] = useState(0);

  useEffect(() => {
    let active = true;
    const seen = new Set();
    (projects || []).forEach(project => {
      const args = argsFor(project);
      if (!args) return;
      const key = args.join('::');
      if (seen.has(key)) return;
      seen.add(key);
      const pending = lookup.ensure(key, args);
      if (pending) {
        pending.then(() => {
          if (active) setLoaded(n => n + 1);
        });
      }
    });
    return () => {
      active = false;
    };
  }, [lookup, projects, argsFor]);

  return project => {
    const args = argsFor(project);
    return args ? lookup.values.get(args.join('::')) : undefined;
  };
}
